import { useMemo } from "react";

import {
    LoopInfoParams,
    JupiterQuoteParams,
    JupiterSwapMode,
    UserLoopFilter,
    StrategyDuration,
    TokenListTag,
    TokenListMetadata,
    UncertaintyType,
    JupiterPriceResponse,
    LoopRouteType,
    UserLoopInfo,
    LoopUnwindType
} from "@bridgesplit/abf-sdk";
import {
    decimalsToBps,
    filterNullableRecord,
    formatNum,
    getUnixTs,
    greaterThan,
    IS_LOCAL_NX_DEV,
    lamportsToUiAmount,
    NullableRecord,
    percentUiToDecimals,
    roundDownToDecimals,
    SortDirection,
    TIME,
    uiAmountToLamports,
    WRAPPED_SOL_MINT,
    bsMath
} from "@bridgesplit/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMemoizedKeyMap } from "@bridgesplit/ui";
import { LoopWindRouteRequest, WindRouteStep, UnwindRouteStep } from "@loopscale/pandora";

import {
    useTokenListMetadataByMints,
    useLoopBestQuoteQuery,
    useLoopInfosQuery,
    useUserLoopsQuery,
    useAllMarketPrincipalStatsQuery,
    useFlashPerpsQuery,
    useJupiterPriceQuoteQuery,
    useLoopWindQuoteIxsQuery,
    useLoopUnwindQuoteIxsQuery,
    LoopUnwindRouteRequestExtended,
    LoopRouteResponseExpanded
} from "../reducers";
import {
    AbfLoanExpanded,
    BestLoopQuoteQuery,
    ExternalUnwindQuoteParams,
    ExternalWindQuoteParams,
    LoopExpanded,
    LoopPositionExpanded,
    LoopQuoteExpanded,
    LoopExpandedBase,
    AbfOrderFundingType,
    PriceFetchType,
    LoopExpandedMeteora,
    ParsedLoanEventExpanded,
    LoanEventActionType,
    LiquidationType
} from "../types";
import { useOraclePrices } from "./pricing";
import { isLoopLoan, useAbfTypesToUiConverter, isLiquidateEvent } from "../utils";
import { getZcCollateral, getZcLedger, isLoanActive, useLoanInfos } from "./loans";
import { getBorrowCapDetails, useMarketBorrowCaps } from "./markets";
import { DEFAULT_STRATEGY_DURATION } from "../constants";
import { useActiveWallet } from "./wallet";
import { useEpochInfo } from "./utils";

export function useLoopsExpanded(params: LoopInfoParams, options?: { skip?: boolean }) {
    const query = useLoopInfosQuery(params, { skip: options?.skip });

    const collateralMints = useMemo(
        () => Object.values(query.data ?? {}).map((loop) => loop.collateralMint),
        [query.data]
    );
    const principalMints = useMemo(
        () => Object.values(query.data ?? {}).map((loop) => loop.principalMint),
        [query.data]
    );

    const allMints = useMemo(() => [...collateralMints, ...principalMints], [collateralMints, principalMints]);
    const { getMetadata, isLoading: metadataLoading } = useTokenListMetadataByMints(allMints);
    const { getOracle, isLoading: marketLoading } = useOraclePrices(allMints, PriceFetchType.Twap);
    const { convertLoopVault, convertMarketPrincipalStats } = useAbfTypesToUiConverter(allMints);
    const { getBorrowCap, isLoading: borrowCapsLoading } = useMarketBorrowCaps(principalMints);

    const marketStatsQuery = useAllMarketPrincipalStatsQuery(undefined, {
        skip: !principalMints.length || options?.skip
    });

    const principalStatsMap = useMemo(
        () => (marketStatsQuery.data ? new Map(Object.entries(marketStatsQuery.data)) : undefined),
        [marketStatsQuery]
    );

    const isLoading =
        query.isLoading || metadataLoading || marketLoading || marketStatsQuery.isLoading || borrowCapsLoading;

    const data = useMemo(() => {
        if (!query.data || isLoading) return undefined;

        return Object.entries(query.data)
            .map(([vaultIdentifier, loopVaultRaw]): NullableRecord<LoopExpanded> => {
                const loopVault = convertLoopVault(loopVaultRaw);
                const borrowCap = getBorrowCap(loopVault.principalMint);
                let principalStats = principalStatsMap?.get(loopVault.principalMint);
                if (principalStats) {
                    principalStats = convertMarketPrincipalStats(loopVault.principalMint, principalStats);
                }

                const common: NullableRecord<LoopExpandedBase> = {
                    vaultIdentifier,
                    loopVault,
                    collateralToken: getMetadata(loopVault.collateralMint),
                    principalToken: getMetadata(loopVault.principalMint),
                    collateralOracle: getOracle(loopVault.collateralMint, PriceFetchType.Spot),
                    principalOracle: getOracle(loopVault.principalMint, PriceFetchType.Spot),
                    principalStats,
                    borrowCap,
                    maxCollateralAmount: loopVault.maxCollateralAmount
                };

                if (!loopVault.feVisible && !params.showHidden && !IS_LOCAL_NX_DEV) {
                    return {
                        ...common,
                        // this will disable the loop in the UI
                        tokenA: undefined
                    };
                }

                if (loopVault.metadata && "lpMint" in loopVault.metadata) {
                    const tokenA = getMetadata(loopVault.metadata.tokenAMint);
                    const tokenB = getMetadata(loopVault.metadata.tokenBMint);
                    return {
                        ...common,
                        meteoraPool: loopVault.metadata,
                        tokenA,
                        tokenB
                    };
                }

                return {
                    ...common
                };
            })
            .filter(filterNullableRecord);
    }, [
        query.data,
        isLoading,
        convertLoopVault,
        getBorrowCap,
        principalStatsMap,
        getMetadata,
        getOracle,
        params.showHidden,
        convertMarketPrincipalStats
    ]);

    const filteredData = useMemo(() => {
        if (!data) return undefined;
        if (params.showHidden) return data;
        return data.filter((loop) => loop.loopVault.feVisible);
    }, [data, params.showHidden]);

    return { data: filteredData, isLoading, isFetching: query.isFetching };
}

export function useUserLoops({
    loopsExpanded,
    filter,
    skip
}: {
    loopsExpanded: LoopExpanded[] | undefined;
    filter: UserLoopFilter;
    skip?: boolean;
}) {
    const query = useUserLoopsQuery(
        {
            userLoopInfo: {
                loopVaults: loopsExpanded?.map((loop) => loop.vaultIdentifier) ?? [],
                ...filter
            }
        },
        {
            skip: !loopsExpanded?.length || skip
        }
    );

    const loanAddresses = useMemo(
        () => (query.data ? query.data.map((userLoop) => userLoop.loanAddress) : []),
        [query.data]
    );

    const { data: loanInfos, isLoading: loanInfosLoading } = useLoanInfos({
        skip: !loanAddresses.length,
        pagination: null,
        loanFilter: {
            loanAddresses,
            orderFundingTypes: [AbfOrderFundingType.Loop],
            borrowers: [],
            lenders: [],
            custodians: [],
            principalMints: [],
            saleEvents: false,
            ignoreRefinanced: false,
            sortSide: SortDirection.Asc
        }
    });
    const loanExpandedMap = useMemoizedKeyMap(loanInfos, (l) => l.loan.address);
    const loopExpandedMap = useMemoizedKeyMap(loopsExpanded, (l) => l.vaultIdentifier);

    const isLoading = query.isLoading || loanInfosLoading;
    const { convertUserLoopInfo } = useAbfTypesToUiConverter([]);

    const data = useMemo(() => {
        if (isLoading) return undefined;
        if (skip || !query.data || !loanInfos) return [];

        return query.data
            .map((userLoop): NullableRecord<LoopPositionExpanded> => {
                const loanExpanded = loanExpandedMap?.get(userLoop.loanAddress);
                const loopExpanded = loopExpandedMap?.get(userLoop.loopVaultIdentifier);
                const userLoopInfo = loopExpanded
                    ? convertUserLoopInfo(userLoop, loopExpanded, loanExpanded)
                    : undefined;
                return {
                    loanAddress: userLoop.loanAddress,
                    userLoopInfo,
                    loanExpanded,
                    loopExpanded
                };
            })
            .filter(filterNullableRecord);
    }, [isLoading, skip, query.data, loanInfos, loanExpandedMap, loopExpandedMap, convertUserLoopInfo]);

    return { data, isLoading };
}

export function useFlashPerps(options?: { skip?: boolean }) {
    return useFlashPerpsQuery(undefined, { skip: options?.skip });
}

export function useUserLoopByLoan(loanExpanded: AbfLoanExpanded | undefined, options?: { skip?: boolean }) {
    const positionQuery = useUserLoopsQuery(
        {
            userLoopInfo: {
                page: 0,
                pageSize: 1,
                active: isLoanActive(loanExpanded),
                loanVaults: loanExpanded ? [loanExpanded.loan.address] : []
            }
        },
        {
            skip: options?.skip || !loanExpanded || !isLoopLoan(loanExpanded)
        }
    );

    const loopPosition = positionQuery.data?.find((userLoop) => userLoop.loanAddress === loanExpanded?.loan.address);
    const loopQuery = useLoopsExpanded(
        { loopVaults: loopPosition ? [loopPosition.loopVaultIdentifier] : undefined, showHidden: true },
        { skip: !loopPosition }
    );
    const loopExpanded = loopQuery.data ? Object.values(loopQuery.data)[0] : undefined;

    const isLoading = positionQuery.isLoading || loopQuery.isLoading;
    const isFetching = positionQuery.isFetching || loopQuery.isFetching;

    const { convertUserLoopInfo } = useAbfTypesToUiConverter([]);

    const data = useMemo((): LoopPositionExpanded | undefined => {
        if (!loanExpanded || !loopPosition || isLoading || !loopExpanded) return undefined;
        const userLoopInfo = convertUserLoopInfo(loopPosition, loopExpanded, loanExpanded);
        return {
            loanAddress: loanExpanded.loan.address,
            userLoopInfo,
            loanExpanded,
            loopExpanded
        };
    }, [loanExpanded, loopPosition, isLoading, loopExpanded, convertUserLoopInfo]);

    return { data, isLoading, isFetching };
}

export function formatLeverage(leverage: number, decimals = 1) {
    return `${formatNum(leverage, { customDecimals: decimals })}x`;
}

export function useWindQuoteAndIxs({
    loopExpanded,
    principalAmount,
    skip,
    slippagePercentDecimals
}: ExternalWindQuoteParams) {
    const { activeWallet } = useActiveWallet();
    const request = useMemo(() => {
        if (!loopExpanded || !principalAmount || !slippagePercentDecimals || !activeWallet || skip) return undefined;
        const inputMint = loopExpanded.principalToken.mint;
        const outputMint = loopExpanded.collateralToken.mint;
        const routeStep: WindRouteStep = {
            inputMint,
            outputMint,
            inputAmount: uiAmountToLamports(principalAmount, loopExpanded.principalToken.decimals),
            ...loopExpanded.loopVault.quoteFetchParams
        };
        const request: LoopWindRouteRequest = {
            slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
            userWallet: activeWallet,
            routeSteps: [routeStep]
        };
        return request;
    }, [loopExpanded, principalAmount, slippagePercentDecimals, activeWallet, skip]);

    return useLoopWindQuoteIxsQuery(request ?? skipToken, {
        skip: !request || skip
    });
}

// temporary type until backend is updated, then will use type exported from pandora
type ExtendedUnwindRouteStep = UnwindRouteStep & {
    collateralDecimals: number;
    principalDecimals: number;
};

const UNWIND_REFETCH_INTERVAL = TIME.MINUTE * 2000;

export function useUnwindQuoteAndIxs({
    loopPositionExpanded,
    skip,
    slippagePercentDecimals,
    unwindType
}: ExternalUnwindQuoteParams) {
    const { activeWallet } = useActiveWallet();

    const request = useMemo(() => {
        if (!loopPositionExpanded || !slippagePercentDecimals || !activeWallet || skip) return undefined;

        const inputMint = loopPositionExpanded.loopExpanded.collateralToken.mint;
        const outputMint = loopPositionExpanded.loopExpanded.principalToken.mint;

        const collateral = getZcCollateral(loopPositionExpanded?.loanExpanded);
        const collateralAmount = collateral?.loanCollateral.amount;

        const collateralAmountLamports = uiAmountToLamports(
            collateralAmount,
            loopPositionExpanded.loopExpanded.collateralToken.decimals
        );

        const ledger = getZcLedger(loopPositionExpanded.loanExpanded);
        if (!ledger) return undefined;
        const ledgerDebtAmount = uiAmountToLamports(
            ledger.ledgerDebt.total,
            loopPositionExpanded.loopExpanded.principalToken.decimals
        );

        const routeStep: ExtendedUnwindRouteStep = {
            inputMint,
            outputMint,
            principalDecimals: loopPositionExpanded.loopExpanded.principalToken.decimals,
            collateralDecimals: loopPositionExpanded.loopExpanded.collateralToken.decimals,
            ledgerDebtAmount,
            totalCollateralAmount: collateralAmountLamports,
            ...loopPositionExpanded.loopExpanded.loopVault.quoteFetchParams
        };
        const request: LoopUnwindRouteRequestExtended = {
            slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
            userWallet: activeWallet,
            routeSteps: [routeStep],
            loanAddress: loopPositionExpanded.loanAddress,
            unwindType
        };
        return request;
    }, [loopPositionExpanded, slippagePercentDecimals, activeWallet, skip, unwindType]);

    return useLoopUnwindQuoteIxsQuery(request ?? skipToken, {
        skip: !request || skip,
        refetchOnMountOrArgChange: true,
        pollingInterval: UNWIND_REFETCH_INTERVAL
    });
}

export const MAX_JUP_ACCOUNTS = 36;

export const DEFAULT_JUP_QUOTE_PARAMS: Omit<
    JupiterQuoteParams,
    "amount" | "slippageBps" | "inputMint" | "outputMint" | "outputMint"
> = {
    maxAccounts: MAX_JUP_ACCOUNTS,
    restrictIntermediateTokens: true,
    swapMode: JupiterSwapMode.ExactIn,
    onlyDirectRoutes: false
};

export function useLoopCollateralPrice({
    tokens,
    options
}: {
    tokens: TokenListMetadata[] | undefined;
    options?: { skip?: boolean };
}) {
    const isActive = !!tokens?.length;

    const { data: jupiterPrices, isError: priceError } = useJupiterPriceQuoteQuery(
        {
            mints: tokens ? tokens.map((token) => token.mint) : []
        },
        {
            skip: options?.skip || !isActive || !tokens
        }
    );

    return { data: jupiterPrices, isError: priceError };
}

export function calculateLtvFromLeverage(leverageMultiplier: number) {
    return 1 - 1 / leverageMultiplier;
}

function getLoopLeveragedAmounts({
    loopExpanded,
    leverageMultiplier,
    collateralAmount
}: {
    loopExpanded: LoopExpanded;
    leverageMultiplier: number;
    collateralAmount: number;
}) {
    const leveragedCollateralAmount = collateralAmount * leverageMultiplier;
    const collateralUsd = loopExpanded.collateralOracle.getUsdAmount(
        leveragedCollateralAmount,
        undefined,
        UncertaintyType.Subtract
    );

    const principalUsd = collateralUsd * calculateLtvFromLeverage(leverageMultiplier);
    const principalAmount = roundDownToDecimals(
        principalUsd / (loopExpanded.principalOracle.usdPrice + loopExpanded.principalOracle.uncertainty),
        loopExpanded.principalToken.decimals
    );

    return {
        principalAmount,
        leveragedCollateralAmount
    };
}

type LoopBestQuoteParams = {
    loopExpanded: LoopExpanded | undefined;
    collateralAmountUi: number | undefined;
    leverageMultiplier: number | undefined;
    slippagePctDecimals?: number;
    strategyDuration: StrategyDuration;
    skip?: boolean;
};
export function useLoopBestQuote(params: LoopBestQuoteParams) {
    const { convertOfferQuote } = useAbfTypesToUiConverter([]);
    const quoteParams = useMemo((): BestLoopQuoteQuery | undefined => {
        if (
            !params.loopExpanded ||
            !params.collateralAmountUi ||
            !isValidLeverage(params.leverageMultiplier) ||
            !params.strategyDuration
        )
            return undefined;
        return {
            loopVault: params.loopExpanded.vaultIdentifier,
            collateralAmount: uiAmountToLamports(
                params.collateralAmountUi,
                params.loopExpanded.collateralToken.decimals
            ),
            duration: params.strategyDuration,
            leverageMultiplier: params.leverageMultiplier
        };
    }, [params]);

    const skip = !quoteParams || params.skip;
    const { data: rawData, ...query } = useLoopBestQuoteQuery(quoteParams ?? skipToken, {
        skip
    });

    const data = useMemo((): LoopQuoteExpanded | null | undefined => {
        if (!params.loopExpanded || !quoteParams || !params.collateralAmountUi) return undefined;
        if (!rawData) return rawData;
        const collateralAmount = params.collateralAmountUi;
        const bestQuote = convertOfferQuote(rawData.bestQuote, params.loopExpanded.principalToken.mint);
        const { principalAmount, leveragedCollateralAmount } = getLoopLeveragedAmounts({
            loopExpanded: params.loopExpanded,
            leverageMultiplier: quoteParams.leverageMultiplier - (params.slippagePctDecimals ?? 0.05),
            collateralAmount
        });
        return {
            ...rawData,
            netApyPct: percentUiToDecimals(rawData.netApyPct),
            bestQuote,
            leverageMultiplier: quoteParams.leverageMultiplier - (params.slippagePctDecimals ?? 0.05),
            collateralAmount,
            principalAmount,
            leveragedCollateralAmount
        };
    }, [
        params.loopExpanded,
        params.collateralAmountUi,
        params.slippagePctDecimals,
        quoteParams,
        rawData,
        convertOfferQuote
    ]);

    return { data, ...query };
}

export function usePrincipalBorrowedWithLeverage({
    loopExpanded,
    loopLeverage,
    totalAmount,
    skip = false
}: {
    loopExpanded: LoopExpanded | undefined;
    loopLeverage: number | undefined;
    totalAmount: number;
    skip?: boolean;
}) {
    const quoteParams = useMemo((): LoopBestQuoteParams => {
        return {
            loopExpanded,
            leverageMultiplier: loopLeverage,
            collateralAmountUi: totalAmount,
            strategyDuration: DEFAULT_STRATEGY_DURATION
        };
    }, [loopExpanded, loopLeverage, totalAmount]);

    const { data: quote, isLoading: isQuoteLoading } = useLoopBestQuote({
        ...quoteParams,
        skip
    });

    const principalBorrowedWithLeverageUsd = useMemo(() => {
        if (!quote?.principalAmount || !loopExpanded?.principalOracle.usdPrice) {
            return undefined;
        }
        return quote.principalAmount * loopExpanded.principalOracle.usdPrice;
    }, [quote, loopExpanded]);

    return {
        principalBorrowedWithLeverageUsd,
        isLoading: isQuoteLoading
    };
}

export function calculateLoopNetApyFromMultiplier({
    collateralYield,
    leverageMultiplier,
    principalBorrowRate
}: {
    collateralYield: number;
    leverageMultiplier: number;
    principalBorrowRate: number;
}) {
    const collateralWeighted = leverageMultiplier;
    const principalWeighted = collateralWeighted * calculateLtvFromLeverage(leverageMultiplier);
    const netApy = collateralWeighted * collateralYield - principalWeighted * principalBorrowRate;
    return netApy;
}

export function getLoopBorrowCapDetails(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded) return undefined;
    return getBorrowCapDetails({
        borrowCap: loopExpanded.borrowCap,
        token: loopExpanded.principalToken,
        principalUtilized: loopExpanded.principalStats.principalUtilized
    });
}

export function useAllUserLoops(filter: UserLoopFilter) {
    const { activeWallet } = useActiveWallet();
    const { data: loops } = useLoopsExpanded({
        showHidden: true
    });

    return useUserLoops({
        loopsExpanded: loops,
        filter,
        skip: !loops || !activeWallet
    });
}

// leverage must be at least 1 to start a loan
export const isValidLeverage = (leverageMultiplier: number | undefined): leverageMultiplier is number =>
    greaterThan(leverageMultiplier, 1);

export interface UnwindTokenStats {
    receiveTokenAmount: number;
    receiveUsdAmount: number;
    tokenToReceive: TokenListMetadata | undefined;
    oraclePercentDifference: number;
    leftOverPrincipal: number | undefined;
    leftOverPrincipalToken: TokenListMetadata | undefined;
    quotePrice: number;
    oraclePrice: number;
}
interface ProfitLossStats {
    profitLossUsd: number;
    profitLossTokenAmount: number | undefined;
    contributions: ReturnType<typeof sumLoopPositionUsdContributions>;
    tokenStats?: UnwindTokenStats | null;
    feeDecimals?: number;
    priceImpactDecimals?: number;
}

export function calculateUnwindStats({
    loopPosition,
    externalQuote,
    jupiterPrices,
    unwindType
}: {
    loopPosition: LoopPositionExpanded;
    externalQuote: LoopRouteResponseExpanded | undefined;
    jupiterPrices: JupiterPriceResponse;
    unwindType: LoopUnwindType;
}): ProfitLossStats {
    const contributions = sumLoopPositionUsdContributions(loopPosition);
    const tokenStats = (() => {
        if (unwindType === LoopUnwindType.Collateral) {
            return calcUnwindStatsCollateral({ loopPosition, externalQuote, jupiterPrices });
        }
        return calcUnwindStatsPrincipal({ loopPosition, externalQuote, jupiterPrices });
    })();

    const profitLossUsd = tokenStats.receiveUsdAmount - contributions.totalContributionUsd;
    let profitLossTokenAmount = undefined;
    if (unwindType !== LoopUnwindType.Principal) {
        profitLossTokenAmount = tokenStats.receiveTokenAmount - contributions.totalCollateralContributionAmount;
    }

    return {
        tokenStats,
        profitLossUsd,
        profitLossTokenAmount,
        contributions,
        feeDecimals: externalQuote?.feeDecimals ?? 0,
        priceImpactDecimals: externalQuote?.priceImpactDecimals
    };
}

function calcUnwindStatsPrincipal({
    loopPosition,
    externalQuote
}: {
    loopPosition: LoopPositionExpanded;
    externalQuote: LoopRouteResponseExpanded | undefined;
    jupiterPrices: JupiterPriceResponse;
}): UnwindTokenStats {
    const { loopExpanded, loanExpanded } = loopPosition;

    const principalSwapped = (() => {
        if (externalQuote) {
            return lamportsToUiAmount(externalQuote.minOutputAmount, loopPosition.loopExpanded.principalToken.decimals);
        }
        return (
            calculateNetLoopPositionUsd({ loopExpanded, loopInfo: loopPosition.userLoopInfo, loanExpanded }) /
            loopExpanded.principalOracle.usdPrice
        );
    })();

    const firstLedger = getZcLedger(loanExpanded);

    const receiveTokenAmount = Math.max(principalSwapped - (firstLedger?.ledgerDebt.total ?? 0), 0);
    const receiveUsdAmount = loopExpanded.principalOracle.getUsdAmount(receiveTokenAmount);
    const tokenToReceive = loopExpanded.principalToken;
    const oraclePrice = loopExpanded.principalOracle.usdPrice;

    const quotePrice = receiveUsdAmount / receiveTokenAmount; //Recieved usd amount uses regular oracle, should be no discrepancy

    const oraclePercentDifference = (quotePrice - oraclePrice) / oraclePrice;

    return {
        receiveTokenAmount,
        receiveUsdAmount,
        tokenToReceive,
        leftOverPrincipal: undefined, // no left over principal for principal unwinds,
        leftOverPrincipalToken: undefined,
        oraclePercentDifference,
        quotePrice,
        oraclePrice
    };
}

function calculateNetLoopPositionUsd({
    loopExpanded,
    loanExpanded
}: {
    loopExpanded: LoopExpanded;
    loopInfo: UserLoopInfo;
    loanExpanded: AbfLoanExpanded;
}) {
    const zcLedger = getZcLedger(loanExpanded);

    const ledgerDebtUsd = (zcLedger?.ledgerDebt.total ?? 0) * loopExpanded.principalOracle.usdPrice;
    const loanCollateralUsd =
        loanExpanded.collateral[0]?.loanCollateral.amount * loopExpanded.collateralOracle.usdPrice;
    return Math.max(loanCollateralUsd - ledgerDebtUsd, 0);
}

function calcUnwindStatsCollateral({
    loopPosition,
    externalQuote,
    jupiterPrices
}: {
    loopPosition: LoopPositionExpanded;
    externalQuote: LoopRouteResponseExpanded | undefined;
    jupiterPrices: JupiterPriceResponse;
}): UnwindTokenStats {
    const { userLoopInfo, loopExpanded, loanExpanded } = loopPosition;

    const inputCollateral = externalQuote
        ? lamportsToUiAmount(externalQuote.inputAmount, loopPosition.loopExpanded.collateralToken.decimals)
        : undefined;

    const minAmountOut = externalQuote?.minOutputAmount;

    const currentDebt = getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0;

    // jup stepper edge case for now
    const leftOverPrincipal = Math.max(
        minAmountOut
            ? lamportsToUiAmount(minAmountOut, loopPosition.loopExpanded.principalToken.decimals) - currentDebt
            : 0,
        0
    );

    const collateralAmount = getZcCollateral(loanExpanded)?.loanCollateral.amount ?? 0;
    const receiveTokenAmount = inputCollateral
        ? Math.max(collateralAmount - inputCollateral, 0)
        : calculateNetLoopPositionUsd({ loopExpanded, loopInfo: userLoopInfo, loanExpanded }) /
          loopExpanded.collateralOracle.usdPrice;

    const receiveUsdAmount =
        loopExpanded.collateralOracle.getUsdAmount(receiveTokenAmount) +
        loopExpanded.principalOracle.getUsdAmount(leftOverPrincipal ?? 0);

    const tokenToReceive = loopExpanded.collateralToken;

    const quotePrice = (() => {
        switch (loopExpanded.loopVault.routeType) {
            case LoopRouteType.Jup:
                return Number(jupiterPrices.data[loopExpanded.collateralToken.mint]?.price ?? 0);
            default:
                return 0; //only jupiter is supported for collateral swaps
        }
    })();

    const oraclePrice = loopExpanded.collateralOracle.usdPrice;

    const oraclePercentDifference = (quotePrice - oraclePrice) / oraclePrice;

    return {
        receiveTokenAmount,
        receiveUsdAmount,
        tokenToReceive,
        oraclePercentDifference,
        leftOverPrincipal,
        leftOverPrincipalToken: loopExpanded.principalToken,
        quotePrice,
        oraclePrice
    };
}

interface HistoricalNpv {
    npvUsd: number | undefined;
    npvTokenAmount: number | undefined;
}

function getHistoricalNpvFromWithdrawalInfo(position: LoopPositionExpanded): HistoricalNpv | undefined {
    const withdrawalInfo = position.userLoopInfo.withdrawalInfo;
    if (!withdrawalInfo) {
        return undefined;
    }
    const withdrawalTokenAmount = withdrawalInfo.withdrawnTokenAmount;

    const lastCloseEvents = getLastCloseEvents(position.loanExpanded.events);
    const lastRemoveCollateralEvent = lastCloseEvents?.lastRemoveCollateralEvent;
    const lastRepayPrincipalEvent = lastCloseEvents?.lastRepayPrincipalEvent;

    const withdrawalTokenPrice =
        withdrawalInfo.withdrawnTokenMint === position.loopExpanded.collateralToken.mint
            ? lastRemoveCollateralEvent?.usdPrice
            : lastRepayPrincipalEvent?.usdPrice;

    const withdrawalTokenUsd = bsMath.mul(withdrawalTokenAmount, withdrawalTokenPrice);

    return {
        npvUsd: withdrawalTokenUsd,
        npvTokenAmount: withdrawalTokenAmount
    };
}

export function calculateHistoricalLoopProfitLoss(position: LoopPositionExpanded): ProfitLossStats {
    const oracleProfitLoss = calculateHistoricalOracleProfitLossStats(position);

    return {
        profitLossUsd: oracleProfitLoss?.usdProfitLoss ?? 0,
        contributions: sumLoopPositionUsdContributions(position),
        profitLossTokenAmount: oracleProfitLoss?.profitLossTokenAmount
    };
}

interface TokenProfitLossValues {
    netCollateralTransfer: EventTokenChangeSummary | undefined;
    additionalBorrows: EventTokenChangeSummary | undefined;
    additionalRepayments: EventTokenChangeSummary | undefined;
    profitLossTokenAmount: number | undefined;
    valueOfAdditionalBorrows: number | undefined;
    valueOfAdditionalRepayments: number | undefined;
    valueOfNetCollateralTransfer: number | undefined;
    usdProfitLoss: number;
}

export function calculateOraclePnlAtTime({
    timeFilteredEvents,
    initialCollateralTokenAmount,
    collateralTokenAmount,
    principalTokenAmount,
    collateralPrice,
    principalPrice
}: {
    timeFilteredEvents: ParsedLoanEventExpanded[] | undefined;
    initialCollateralTokenAmount: number;
    collateralTokenAmount: number;
    principalTokenAmount: number;
    collateralPrice: number;
    principalPrice: number;
}) {
    if (!timeFilteredEvents) return undefined;
    const netCollateralTransfer = getNetCollateralTransfersfromEvents(timeFilteredEvents, initialCollateralTokenAmount);

    const additionalBorrows = calculateAdditionalBorrowsFromEvents(timeFilteredEvents);
    const additionalRepayments = calculateLoopRepayments(timeFilteredEvents);

    const collateralValueUsd = bsMath.mul(collateralTokenAmount, collateralPrice);
    const principalValueUsd = bsMath.mul(principalTokenAmount, principalPrice);

    const npvUsd = bsMath.sub(collateralValueUsd, principalValueUsd);
    const npvTokenAmount = bsMath.div(npvUsd, collateralPrice);

    return calculateOraclePnl({
        npvUsd: npvUsd ?? 0,
        npvTokenAmount,
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        collateralPrice,
        events: timeFilteredEvents
    });
}

export function calculateOracleProfitLossStats(
    position: LoopPositionExpanded | undefined
): TokenProfitLossValues | undefined {
    if (!position) return undefined;
    const netCollateralTransfer = getNetCollateralTransfersfromEvents(
        position.loanExpanded.events,
        position.userLoopInfo?.initialCollateralAmount
    );
    const additionalBorrows = calculateAdditionalBorrowsFromEvents(position?.loanExpanded.events);
    const additionalRepayments = calculateLoopRepayments(position?.loanExpanded.events);

    const npvTokenAmount = position.userLoopInfo?.netPositionValue
        ? bsMath.div(position.userLoopInfo?.netPositionValue, position.loopExpanded.collateralOracle.usdPrice)
        : 0;

    return calculateOraclePnl({
        npvUsd: position.userLoopInfo?.netPositionValue ?? 0,
        npvTokenAmount,
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        collateralPrice: position.loopExpanded.collateralOracle.usdPrice,
        events: position.loanExpanded.events
    });
}

function calculateOraclePnl({
    npvUsd,
    npvTokenAmount,
    netCollateralTransfer,
    additionalBorrows,
    additionalRepayments,
    collateralPrice,
    events,
    isPrincipalNpvToken
}: {
    npvUsd: number;
    npvTokenAmount: number;
    netCollateralTransfer: EventTokenChangeSummary | undefined;
    additionalBorrows: EventTokenChangeSummary | undefined;
    additionalRepayments: EventTokenChangeSummary | undefined;
    collateralPrice: number;
    events: ParsedLoanEventExpanded[];
    isPrincipalNpvToken?: boolean;
}) {
    const valueOfNetCollateralTransfer = bsMath.mul(
        netCollateralTransfer?.tokenAmount,
        netCollateralTransfer?.weightedAverageTokenAmountPrice
    );

    const valueOfAdditionalBorrows = bsMath.mul(
        additionalBorrows?.tokenAmount,
        additionalBorrows?.weightedAverageTokenAmountPrice
    );

    const valueOfAdditionalRepayments = bsMath.mul(
        additionalRepayments?.tokenAmount,
        additionalRepayments?.weightedAverageTokenAmountPrice
    );

    const netAdditionalPrincipalValue = bsMath.sub(valueOfAdditionalBorrows, valueOfAdditionalRepayments);

    const collateralTokenAmountOfAdditionalBorrows = bsMath.div(netAdditionalPrincipalValue, collateralPrice);

    const lastCloseEvents = getLastCloseEvents(events);
    const lastRepayPrincipalEvent = lastCloseEvents?.lastRepayPrincipalEvent;

    const usdProfitLoss = bsMath.sub(bsMath.add(npvUsd, netAdditionalPrincipalValue), valueOfNetCollateralTransfer);

    // npvTokenAmount can be in principal or collateral. so if we are subtracting net flows, we need to convert it all to dollars, and the back into the principal price at swap
    const profitLossTokenAmount = isPrincipalNpvToken
        ? bsMath.div(usdProfitLoss, lastRepayPrincipalEvent?.usdPrice)
        : bsMath.sub(
              bsMath.add(npvTokenAmount, collateralTokenAmountOfAdditionalBorrows),
              netCollateralTransfer?.tokenAmount
          );

    return {
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        profitLossTokenAmount,
        valueOfAdditionalBorrows,
        valueOfAdditionalRepayments,
        valueOfNetCollateralTransfer,
        usdProfitLoss: usdProfitLoss ?? 0
    };
}

export function calculateHistoricalOracleProfitLossStats(
    position: LoopPositionExpanded
): TokenProfitLossValues | undefined {
    // if the loop has a withdrawal info, we can use that to get the net position value
    // otherwise we can imply the net position value from the last two close events

    const historicalNpv = position.userLoopInfo.withdrawalInfo
        ? getHistoricalNpvFromWithdrawalInfo(position)
        : getImpliedNpvBeforeCloseEvents(position);

    const netCollateralTransfer = getNetCollateralTransfersfromEvents(
        position.loanExpanded.events,
        position.userLoopInfo?.initialCollateralAmount,
        true
    );
    const additionalBorrows = calculateAdditionalBorrowsFromEvents(position?.loanExpanded.events);
    const additionalRepayments = calculateLoopRepayments(position?.loanExpanded.events, true);

    return calculateOraclePnl({
        npvUsd: historicalNpv?.npvUsd ?? 0,
        npvTokenAmount: historicalNpv?.npvTokenAmount ?? 0,
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        collateralPrice: position.loopExpanded.collateralOracle.usdPrice,
        events: position.loanExpanded.events,
        isPrincipalNpvToken:
            position.userLoopInfo.withdrawalInfo?.withdrawnTokenMint === position.loopExpanded.principalToken.mint
    });
}

export function calculateLoopOracleProfitLoss(position: LoopPositionExpanded): ProfitLossStats | undefined {
    const tokenProfitLoss = calculateOracleProfitLossStats(position);

    if (!tokenProfitLoss) return undefined;

    const { usdProfitLoss, profitLossTokenAmount } = tokenProfitLoss;

    return {
        profitLossUsd: usdProfitLoss,
        contributions: sumLoopPositionUsdContributions(position),
        profitLossTokenAmount: profitLossTokenAmount ?? 0
    };
}

export function sumLoopPositionUsdContributions(loopPosition: LoopPositionExpanded) {
    // cron updates rates every 15 minutes so oracle price might be more recent than userLoopInfo.initialCollateralPrice
    const startingCollateralPrice = getLoopFirstAddCollateralEvent(loopPosition.loanExpanded.events)?.usdPrice;

    const isActive = isLoanActive(loopPosition.loanExpanded);

    // is !isActive we skip the last remove collateral event.
    // its closed so we dont want the last remove event to look like a partial withdrawal
    const collateralDeposits = getNetCollateralTransfersfromEvents(
        loopPosition.loanExpanded.events,
        loopPosition.userLoopInfo.initialCollateralAmount,
        !isActive
    );

    const repayments = calculateLoopRepayments(loopPosition.loanExpanded.events);

    const initialDepositAmount = loopPosition.userLoopInfo.initialCollateralAmount;

    const initialCollateralUsd = initialDepositAmount * (startingCollateralPrice ?? 0);

    return {
        initialCollateralUsd,
        initialCollateralAmount: initialDepositAmount,
        totalCollateralTopUpsUsd: (collateralDeposits?.tokenAmountUsd ?? 0) - initialCollateralUsd,
        totalCollateralTopUpsAmount: (collateralDeposits?.tokenAmount ?? 0) - initialDepositAmount,
        totalCollateralContributionAmount: collateralDeposits?.tokenAmount ?? 0,
        repaymentsUsd: repayments?.tokenAmountUsd ?? 0,
        totalContributionUsd:
            initialCollateralUsd + (collateralDeposits?.tokenAmountUsd ?? 0) + (repayments?.tokenAmountUsd ?? 0),
        totalPaidUsd: repayments?.tokenAmountUsd ?? 0,
        additionalDepositsUsd: (collateralDeposits?.tokenAmountUsd ?? 0) + (repayments?.tokenAmountUsd ?? 0)
    };
}

export function isLstLoop(loop: LoopExpanded) {
    return loop.collateralToken.tags?.includes(TokenListTag.LST) && loop.principalToken.mint === WRAPPED_SOL_MINT;
}

export function isExponentLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Exponent;
}

export function isRatexLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Ratex;
}

export function isAdrenaLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Adrena;
}

export function isMeteora(loopExpanded: LoopExpanded): loopExpanded is LoopExpandedMeteora {
    return "meteoraPool" in loopExpanded && "tokenA" in loopExpanded && "tokenB" in loopExpanded;
}

export function useCalculateLstBreakevenTime({
    totalCollateralUsd,
    contributedCollateralUsd,
    totalDebtUsd,
    lstApy,
    loanApy
}: {
    totalCollateralUsd: number | undefined;
    contributedCollateralUsd: number | undefined;
    totalDebtUsd: number | undefined;
    lstApy: number | undefined;
    loanApy: number | undefined;
}): number | undefined {
    const { totalEpochDurationSeconds } = useEpochInfo();

    return useMemo(() => {
        if (
            totalCollateralUsd === undefined ||
            lstApy === undefined ||
            totalDebtUsd === undefined ||
            loanApy === undefined ||
            totalEpochDurationSeconds === undefined ||
            contributedCollateralUsd === undefined ||
            lstApy < 0 ||
            lstApy < loanApy
        ) {
            return undefined;
        }

        const secondsInYear = 365 * 24 * 60 * 60;
        const secondsInMonths = 30 * 24 * 60 * 60; // to return early if loop hasnt broken even in a month's time

        const lstRatePerEpoch = lstApy * (totalEpochDurationSeconds / secondsInYear);
        const debtRatePerEpoch = loanApy * (totalEpochDurationSeconds / secondsInYear);

        let cumulativeDebtUsd = totalDebtUsd ?? 0;
        let cumulativeCollateralUsd = totalCollateralUsd;

        // Simulate forward
        let duration = 0;

        while (duration < secondsInMonths) {
            duration += totalEpochDurationSeconds;
            const rewardThisEpochUsd = cumulativeCollateralUsd * lstRatePerEpoch;
            const debtThisEpochUsd = cumulativeDebtUsd * debtRatePerEpoch;

            cumulativeCollateralUsd += rewardThisEpochUsd;
            cumulativeDebtUsd += debtThisEpochUsd;
            const breakEven = cumulativeDebtUsd + contributedCollateralUsd;
            if (cumulativeCollateralUsd >= breakEven) {
                break;
            }
        }
        return duration >= secondsInMonths ? undefined : duration + totalEpochDurationSeconds;
    }, [totalCollateralUsd, contributedCollateralUsd, totalDebtUsd, lstApy, loanApy, totalEpochDurationSeconds]);
}

export interface EventTokenChangeSummary {
    tokenAmount: number;
    tokenAmountUsd: number;
    weightedAverageTokenAmountPrice: number;
}

export function calculateWithdrawalsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, LoanEventActionType.RemoveCollateral);
}

export function calculateAdditionalBorrowsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, LoanEventActionType.BorrowPrincipal, { skipFirstEvent: true });
}

export function calculateLoopRepayments(
    events: ParsedLoanEventExpanded[] | undefined,
    isHistorical = false
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, LoanEventActionType.RepayPrincipal, { skipLastEvent: isHistorical });
}

// Skip first add collateral event - it's the loop start (initial + leveraged amounts), not an additional deposit
export function calculateAdditionalCollateralDeposits(
    events: ParsedLoanEventExpanded[] | undefined
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, LoanEventActionType.AddCollateral, { skipFirstEvent: true });
}

export function getNetCollateralTransfersfromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    initialCollateralAmount: number,
    isHistorical = false
) {
    if (!events) return undefined;

    const sortedEvents = [...events].sort((a, b) => a.eventTime - b.eventTime);
    const filteredEvents = sortedEvents.filter(
        (e) => e.action === LoanEventActionType.AddCollateral || e.action === LoanEventActionType.RemoveCollateral
    );

    // If historical, exclude the last remove collateral event
    if (isHistorical) {
        const lastRemoveIndex = [...filteredEvents]
            .reverse()
            .findIndex((e) => e.action === LoanEventActionType.RemoveCollateral);
        if (lastRemoveIndex !== -1) {
            filteredEvents.splice(filteredEvents.length - 1 - lastRemoveIndex, 1);
        }
    }

    const firstAddCollateralEvent = filteredEvents.findIndex((e) => e.action === LoanEventActionType.AddCollateral);
    const firstRemovedCollateralEvent = filteredEvents.splice(firstAddCollateralEvent, 1)[0];

    const totalEvents = filteredEvents.reduce(
        (acc, event) => {
            const tokenAmount = event.amount ?? 0;
            const principalPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = bsMath.mul(tokenAmount, principalPrice) ?? 0;

            if (event.action === LoanEventActionType.AddCollateral) {
                return {
                    tokenAmount: acc.tokenAmount + tokenAmount,
                    tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0
                };
            } else {
                return {
                    tokenAmount: acc.tokenAmount - tokenAmount,
                    tokenAmountUsd: acc.tokenAmountUsd - tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0
                };
            }
        },
        { tokenAmount: 0, tokenAmountUsd: 0, weightedAverageTokenAmountPrice: 0 }
    );

    totalEvents.tokenAmount += initialCollateralAmount;
    totalEvents.tokenAmountUsd += bsMath.mul(firstRemovedCollateralEvent?.usdPrice, initialCollateralAmount) ?? 0;

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
    }

    return totalEvents;
}

export function calculateTokenChangesFromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    actionType: LoanEventActionType,
    skipOptions: { skipFirstEvent?: boolean; skipLastEvent?: boolean } = { skipFirstEvent: false, skipLastEvent: false }
    // for some callers need to skip the first or last event
    // either for manual calculations or historical calculations
): EventTokenChangeSummary | undefined {
    if (!events) return undefined;

    const sortedEvents = [...events].sort((a, b) => a.eventTime - b.eventTime);
    const filteredEvents = sortedEvents.filter((e) => e.action === actionType);

    const totalEvents = filteredEvents.reduce(
        (acc, event, index) => {
            if (skipOptions.skipFirstEvent && index === 0) {
                return acc;
            }
            if (skipOptions.skipLastEvent && index === filteredEvents.length - 1) {
                return acc;
            }
            const tokenAmount = event.amount ?? 0;
            const tokenPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = event.amount * tokenPrice;

            return {
                tokenAmount: acc.tokenAmount + tokenAmount,
                tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                weightedAverageTokenAmountPrice: 0
            };
        },
        { tokenAmount: 0, tokenAmountUsd: 0, weightedAverageTokenAmountPrice: 0 }
    );

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
    }

    return totalEvents;
}

function generatePrincipalLiquidatedInfo(event: ParsedLoanEventExpanded): number {
    const data = event.actionMetadata[LoanEventActionType.Liquidate];
    const { preRepaymentPrincipalLedgerState, postRepaymentPrincipalLedgerState } = data.repaymentEventMetadata;
    const principalDelta = Math.abs(
        postRepaymentPrincipalLedgerState.principalRepaid - preRepaymentPrincipalLedgerState.principalRepaid
    );
    // const interestDelta = Math.abs(
    //     postRepaymentPrincipalLedgerState.interestDue - preRepaymentPrincipalLedgerState.interestDue
    // );
    return principalDelta; //+ interestDelta;
}

export function getLoopFirstAddCollateralEvent(
    events: ParsedLoanEventExpanded[] | undefined
): ParsedLoanEventExpanded | undefined {
    const sortedEvents = events?.sort((a, b) => a.eventTime - b.eventTime);
    const depositEvent = sortedEvents?.find((e) => e.action === LoanEventActionType.AddCollateral);
    return depositEvent;
}
export function getLoopFirstBorrowEvent(
    events: ParsedLoanEventExpanded[] | undefined
): ParsedLoanEventExpanded | undefined {
    const sortedEvents = events?.sort((a, b) => a.eventTime - b.eventTime);
    const depositEvent = sortedEvents?.find((e) => e.action === LoanEventActionType.BorrowPrincipal);
    return depositEvent;
}
interface TotalLoopLiquidationSummary {
    collateralLiquidated: number;
    collateralLiquidatedUsd: number;
    averageCollateralPrice: number;
    principalRepaidFromLiquidation: number;
    principalAmountUsd: number;
    averagePrincipalPrice: number;
}

export function calculateTotalLiquidations(
    events: ParsedLoanEventExpanded[] | undefined
): TotalLoopLiquidationSummary | undefined {
    const liquidationEvents = events?.filter((e) => isLiquidateEvent(e.action));
    const partialLiquidationEvents = liquidationEvents?.filter(
        (e) => e.actionMetadata[LoanEventActionType.Liquidate].liquidationType === LiquidationType.PartialLiquidation
    );
    const totalLiquidations = partialLiquidationEvents?.reduce(
        (acc, event) => {
            const collateralAmount = event.actionMetadata[LoanEventActionType.Liquidate].collateralTransfers[0].amount;
            const collateralPrice = event.actionMetadata[LoanEventActionType.Liquidate].collateralTransfers[0].price;
            const collateralAmountUsd = collateralPrice * collateralAmount;
            const principalRepaidFromLiquidation = generatePrincipalLiquidatedInfo(event);
            const principalPriceLiquidated = event.usdPrice ?? 0;
            const principalAmountUsd = principalRepaidFromLiquidation * principalPriceLiquidated;
            return {
                collateralLiquidated: acc.collateralLiquidated + collateralAmount,
                collateralLiquidatedUsd: acc.collateralLiquidatedUsd + collateralAmountUsd,
                averageCollateralPrice: 0,
                principalRepaidFromLiquidation: acc.principalRepaidFromLiquidation + principalRepaidFromLiquidation,
                principalAmountUsd: acc.principalAmountUsd + principalAmountUsd,
                averagePrincipalPrice: 0
            };
        },
        {
            collateralLiquidated: 0,
            collateralLiquidatedUsd: 0,
            averageCollateralPrice: 0,
            principalRepaidFromLiquidation: 0,
            principalAmountUsd: 0,
            averagePrincipalPrice: 0
        }
    );
    if (totalLiquidations && totalLiquidations.collateralLiquidated > 0) {
        totalLiquidations.averageCollateralPrice =
            totalLiquidations.collateralLiquidatedUsd / totalLiquidations.collateralLiquidated;
    }
    if (totalLiquidations && totalLiquidations.principalRepaidFromLiquidation > 0) {
        totalLiquidations.averagePrincipalPrice =
            totalLiquidations.principalAmountUsd / totalLiquidations.principalRepaidFromLiquidation;
    }
    return totalLiquidations;
}

export function getLoopStartSwapPrice(loopPosition: LoopPositionExpanded | undefined) {
    const firstBorrowEvent = getLoopFirstBorrowEvent(loopPosition?.loanExpanded.events);
    const firstAddCollateralEvent = getLoopFirstAddCollateralEvent(loopPosition?.loanExpanded.events);
    if (!firstBorrowEvent || !firstAddCollateralEvent) return undefined;

    const borrowedAmount = firstBorrowEvent.amount;

    const swappedCollateralAmount = bsMath.sub(
        firstAddCollateralEvent.amount,
        loopPosition?.userLoopInfo?.initialCollateralAmount ?? 0
    );

    const usdSpent = bsMath.mul(borrowedAmount, firstBorrowEvent.usdPrice);

    const effectiveSwapPrice = bsMath.div(usdSpent, swappedCollateralAmount);

    return effectiveSwapPrice;
}

export function getNetPositionValueTokenAmount(loopPosition: LoopPositionExpanded | undefined) {
    const netPositionValue = loopPosition?.userLoopInfo.netPositionValue;
    const collateralOracle = loopPosition?.loopExpanded.collateralOracle;
    const netPositionValueTokenAmount = bsMath.div(netPositionValue, collateralOracle?.usdPrice);
    return netPositionValueTokenAmount;
}

export const MIN_TIME_FOR_RECENT_START = TIME.MINUTE * 30;

export function getLoopRecentStartTime(loopPosition: LoopPositionExpanded | undefined): boolean | undefined {
    if (!loopPosition) return false;
    const recentStartTime =
        Math.max(getUnixTs() - loopPosition.loanExpanded?.loan.startTime, 0) < MIN_TIME_FOR_RECENT_START;
    return recentStartTime;
}

function getLastCloseEvents(events: ParsedLoanEventExpanded[] | undefined) {
    if (!events) return undefined;
    const sortedEvents = [...events].sort((a, b) => b.eventTime - a.eventTime);

    const lastRepayPrincipalEvent = sortedEvents.find((e) => e.action === LoanEventActionType.RepayPrincipal);
    const lastRemoveCollateralEvent = sortedEvents.find((e) => e.action === LoanEventActionType.RemoveCollateral);

    return {
        lastRepayPrincipalEvent,
        lastRemoveCollateralEvent
    };
}

function getImpliedNpvBeforeCloseEvents(position: LoopPositionExpanded): HistoricalNpv {
    const events = getLastCloseEvents(position.loanExpanded.events);

    const lastRepayPrincipalEvent = events?.lastRepayPrincipalEvent;
    const lastRemoveCollateralEvent = events?.lastRemoveCollateralEvent;

    const lastRepayPrincipalEventValue = bsMath.mul(lastRepayPrincipalEvent?.amount, lastRepayPrincipalEvent?.usdPrice);

    const collateralTokensUsedToRepayDebt = bsMath.div(
        lastRepayPrincipalEventValue,
        lastRemoveCollateralEvent?.usdPrice
    );

    const impliedTokenNpv = bsMath.sub(lastRemoveCollateralEvent?.amount, collateralTokensUsedToRepayDebt);

    const impliedUsdNpv = bsMath.mul(impliedTokenNpv, lastRemoveCollateralEvent?.usdPrice);

    return {
        npvTokenAmount: impliedTokenNpv,
        npvUsd: impliedUsdNpv
    };
}
